import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  correctTrack,
  fetchTrack,
  keepTrack,
  removeTrack,
  type TrackView,
} from "../../services/http.js";

/**
 * One track, and the three things that can be done about it.
 *
 * # The sources are beside the field, and that is the whole design
 *
 * ADR 0012 lets a curator overrule two machines about a year. A curator who
 * cannot see what the machines said is not overruling them, they are guessing —
 * so `track_year_sources` is rendered *next to* the year input, with each
 * source's reference, rather than being provenance the database keeps and nobody
 * reads.
 *
 * # A year is a different act from a title
 *
 * Setting the year makes the track `curated`, exempt from the two-source rule
 * and attributed to the account that did it. Fixing a typo in a title does none
 * of that. The form says so, because a curator who thinks they are fixing a typo
 * should not be quietly changing what `verified` means for that card.
 */
export function TrackPanel({
  trackId,
  onClose,
  onDecided,
}: {
  trackId: string;
  onClose: () => void;
  onDecided: () => void;
}) {
  const { t } = useTranslation();
  const [track, setTrack] = useState<TrackView | null>(null);
  const [year, setYear] = useState("");
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchTrack(trackId).then(
      (loaded) => {
        if (!cancelled) {
          setTrack(loaded);
          setTitle(loaded.title);
          setArtist(loaded.artist_credit);
          setYear(loaded.year === null ? "" : String(loaded.year));
        }
      },
      () => {
        if (!cancelled) {
          setError(t("admin.track.readFailed"));
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [trackId, t]);

  const act = (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    void action()
      .then(onDecided)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : t("round.refused"));
        setBusy(false);
      });
  };

  if (error && !track) {
    return <p className="text-sm text-wrong-500">{error}</p>;
  }
  if (!track) {
    return <p className="text-sm text-white/50">{t("admin.track.reading")}</p>;
  }

  // Only what changed is sent: a form that restates every field overwrites
  // whatever somebody else fixed last week.
  const correction = () => {
    const changes: {
      year?: number;
      title?: string;
      artist_credit?: string;
      note?: string;
    } = {};
    if (year.length === 4 && Number(year) !== track.year) {
      changes.year = Number(year);
    }
    if (title.trim() && title.trim() !== track.title) {
      changes.title = title.trim();
    }
    if (artist.trim() && artist.trim() !== track.artist_credit) {
      changes.artist_credit = artist.trim();
    }
    if (note.trim()) {
      changes.note = note.trim();
    }
    return changes;
  };

  const changing = correction();
  const nothingToDo =
    changing.year === undefined &&
    changing.title === undefined &&
    changing.artist_credit === undefined;

  return (
    <article className="rounded-lg border border-flash-500/40 bg-stage-900 p-4">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="min-w-0 truncate text-lg font-semibold">
          {track.title} <span className="text-white/50">— {track.artist_credit}</span>
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-sm text-white/50"
        >
          {t("admin.track.close")}
        </button>
      </header>
      <p className="mt-1 text-xs text-white/50">
        {track.confidence}
        {track.year !== null && ` · ${t("admin.track.dealtAs", { year: track.year })}`}
        {track.suspended && ` · ${t("admin.queue.suspended")}`}
        {track.removed && ` · ${t("admin.track.removed")}`}
      </p>

      <section aria-label={t("admin.track.sources")} className="mt-3">
        <h3 className="text-xs uppercase tracking-wide text-white/40">
          {t("admin.track.sources")}
        </h3>
        {track.sources.length === 0 ? (
          <p className="mt-1 text-xs text-white/40">{t("admin.track.noSources")}</p>
        ) : (
          <ul className="mt-1 flex flex-col gap-1 text-xs">
            {track.sources.map((source) => (
              <li key={source.source} className="tabular-nums">
                <Trans
                  i18nKey="admin.track.sourceSaid"
                  values={{ source: source.source, year: source.year }}
                  components={{ name: <span className="font-medium" /> }}
                />
                {source.reference && (
                  <span className="text-white/40"> · {source.reference}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {track.votes.length > 0 && (
        <section aria-label={t("admin.track.players")} className="mt-3">
          <h3 className="text-xs uppercase tracking-wide text-white/40">
            {t("admin.track.players")}
          </h3>
          <ul className="mt-1 flex flex-col gap-1 text-xs text-white/60">
            {track.votes.map((vote, index) => (
              <li key={`${vote.party_id ?? "gone"}-${index}`}>
                {vote.verdict === "off"
                  ? t("admin.track.verdictOff")
                  : t("admin.track.verdictFine")}
                {/* The party, not the person: the rule counts rooms, and a name
                    adds nothing to the decision. */}
                {vote.party_id && (
                  <span className="text-white/30">
                    {" "}
                    · {t("admin.track.party", { id: vote.party_id.slice(0, 8) })}
                  </span>
                )}
                {vote.reason && <span className="text-white/50"> — {vote.reason}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {track.decisions.length > 0 && (
        <section aria-label={t("admin.track.decidedBefore")} className="mt-3">
          <h3 className="text-xs uppercase tracking-wide text-white/40">
            {t("admin.track.decidedBefore")}
          </h3>
          <ul className="mt-1 flex flex-col gap-1 text-xs text-white/50">
            {track.decisions.map((decision, index) => (
              <li key={`${decision.decided_at}-${index}`}>
                {decision.action}
                {decision.note && ` — ${decision.note}`}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label={t("admin.track.correct")} className="mt-4 flex flex-col gap-2">
        <label className="text-xs text-white/50" htmlFor={`year-${trackId}`}>
          <Trans i18nKey="admin.track.yearLabel" components={{ curated: <strong /> }} />
        </label>
        <input
          id={`year-${trackId}`}
          inputMode="numeric"
          value={year}
          onChange={(event) => {
            setYear(event.target.value.replace(/\D/g, "").slice(0, 4));
          }}
          className="min-h-12 w-28 rounded-lg border border-white/15 bg-stage-950 px-3 tabular-nums"
        />

        <label className="text-xs text-white/50" htmlFor={`title-${trackId}`}>
          {t("tally.title")}
        </label>
        <input
          id={`title-${trackId}`}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          className="min-h-12 rounded-lg border border-white/15 bg-stage-950 px-3"
        />

        <label className="text-xs text-white/50" htmlFor={`artist-${trackId}`}>
          {t("tally.artist")}
        </label>
        <input
          id={`artist-${trackId}`}
          value={artist}
          onChange={(event) => {
            setArtist(event.target.value);
          }}
          className="min-h-12 rounded-lg border border-white/15 bg-stage-950 px-3"
        />

        <label className="text-xs text-white/50" htmlFor={`note-${trackId}`}>
          {t("admin.track.noteLabel")}
        </label>
        <input
          id={`note-${trackId}`}
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
          className="min-h-12 rounded-lg border border-white/15 bg-stage-950 px-3"
        />
      </section>

      {error && (
        <p role="alert" className="mt-2 text-sm text-wrong-500">
          {error}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || nothingToDo}
          onClick={() => {
            act(() => correctTrack(trackId, changing));
          }}
          className="min-h-12 flex-1 rounded-lg bg-flash-500 px-4 font-semibold text-stage-950 disabled:opacity-40"
        >
          {t("admin.track.correct")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            act(() => keepTrack(trackId, note.trim() || null));
          }}
          className="min-h-12 flex-1 rounded-lg border border-white/25 px-4 font-medium disabled:opacity-40"
        >
          {t("admin.track.keep")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            act(() => removeTrack(trackId, note.trim() || null));
          }}
          className="min-h-12 flex-1 rounded-lg border border-wrong-500/60 px-4 font-medium text-wrong-500 disabled:opacity-40"
        >
          {t("admin.track.remove")}
        </button>
      </div>
    </article>
  );
}
